import React from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import { useEffect } from 'react'

import AppStateProvider from './context/AppState/AppStateProvider'
import { AuthStateProvider } from './context/auth'
import UserContextProvider from './context/user'
import GoogleAnalyticsWrapper from 'layouts/GoogleAnalyticsWrapper'
import { SignOut } from 'pages/Auth/SignOut'
import LandingPage from 'pages/LandingPage'
import routes from './routes'
import theme from './themes/theme'

import { ForgotPassword } from 'pages/Auth/ForgotPassword'
import MyAccount from 'pages/MyAccount'
import MyBusiness from 'pages/MyBusiness'
import ClaimBusiness from 'pages/ClaimBusiness'
import { WelcomePage } from 'pages/Welcome'
import VaultPage from 'pages/Vault'
import BeneficialForm from 'pages/BeneficialInfoForm'
import BusinessDetails from './pages/BusinessDetails'
import ClaimBusinessDialog from './pages/ClaimBusiness'
import SocialPage from 'pages/SocialPage'
import BusinessResourceCenterCard from 'components/LandingPage/BusinessResourceCenterCard'
import IdleTimer from 'components/Common/IdleTimer'

import '@progress/kendo-theme-default/dist/all.css'
import './App.css'
import VaultVerification from 'components/Vault/VaultVerification'
import EmailVerify from 'pages/AuthLayout/emailVerify'
import PasswordVerify from 'pages/AuthLayout/passwordVerify'
import ResendEmail from 'pages/AuthLayout/resendEmail'
import CreatePassword from 'pages/AuthLayout/createPassword'
import OTPVerify from 'pages/AuthLayout/otpVerify'
import { StyledEngineProvider } from '@mui/material'
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n'
import ApproveShare, { DeclineShare } from 'components/Vault/VaultBOD/ApproveDeclineShare'

import NameVerifyMob from 'pages/AuthLayout/NameVerifyMob'
import PasswordVerifyMob from 'pages/AuthLayout/PasswordVerifyMob'
import AccountVerificationMob from 'components/Auth/AccountVerificationMob'
import MobileCheck from 'pages/MobileCheck/MobileCheck'
import { getDeviceInformation } from 'utils/helper'
import AuthLayout from 'pages/AuthLayout'
import UseBrowserLayout from 'pages/UseBrowserLayout'
import { datadogRum } from '@datadog/browser-rum'

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MzSnackbarProvider } from "@markaaz/ui/MzToast";
import BusinessLookupPage from 'pages/BusinessLookupPage'
import GoldenRecordsPage from 'pages/GoldenRecordsPage'
import ContractTermsPage from 'pages/Settings/ContractTermsPage'
import DecisioningPage from 'pages/Settings/DecisioningPage'
import MatchConfidencePage from 'pages/Settings/MatchConfidencePage'
import NameMatchingPage from 'pages/Settings/NameMatchingPage'
import FileValidationPage from 'pages/Settings/FileValidationPage'
import UserManagement from 'pages/UserManagement/UserManagementPage'
import MonitoringReportsPage from 'pages/Reports/MonitoringReportsPage'
import BatchConfigurationPage from 'pages/Reports/BatchSummaryPage'
import LoginPage from "pages/AuthV2/LoginPage";

function App() {
  const env = process.env['REACT_APP_DATA_DOG_ENV']

  datadogRum.init({
    applicationId: '53491283-7177-4ad6-9a0e-cdbb62ffe01a',
    clientToken: 'pubd84bc9457d53557ce56ef30947143c2f',
    site: 'datadoghq.com',
    service: 'dashboard',
    env: env,
    version: '1.14.15',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  })

  datadogRum.startSessionReplayRecording()

  const resize = () => {
    const isAndroid = getDeviceInformation(window).includes('Android')
    const isIphone = getDeviceInformation(window).includes('iPhone')
    const isIpad = getDeviceInformation(window).includes('iPad' || 'Mac') || window.navigator.platform.includes('iPad')
    const isMobile = isAndroid || isIphone || isIpad
    const getAuthRoute = window.location.pathname.includes('auth')
    if (isMobile && window.location.pathname !== '/use-browser') {
      const isAuthRoute =
        !getAuthRoute ||
        window.location.pathname === '/auth/sign-up' ||
        window.location.pathname === '/auth/forgot-password'
      if (isAuthRoute) {
        window.location = `${window.location.origin}/use-browser`
      }
    }
  }

  useEffect(() => {
    window.addEventListener('load', resize)
    window.addEventListener('resize', resize)
  }, [])

  const IdleTimerWrapper = (COMPONENT, isVault = false) => {
    return (
      <>
        <IdleTimer
          maxIdleTime={
            isVault ? process.env['REACT_APP_MAX_IDLE_TIME_VAULT'] : process.env['REACT_APP_MAX_IDLE_TIME_DASHBOARD']
          }
        />
        {COMPONENT}
      </>
    )
  }

  if (process.env['REACT_APP_SITE_PLACEHOLDER'] === '1') {
    return (
      <div className="col-md-12 d-flex justify-content-center">
        <img
          className="img-fluid"
          src={process.env['REACT_APP_BRC_IMAGE_URL'] + '/site-placeholder/placeholder.png'}
          usemap="#image-map"
          alt="placeholder"
        />

        <map name="image-map">
          <area target="" alt="" title="" href="https://markaaz.com/dashboard/" coords="1079,1079,0,984" shape="rect" />
        </map>
      </div>
    )
  } else {
    return (
      <I18nextProvider i18n={i18n}>
        <Router>
          <GoogleAnalyticsWrapper>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <AppStateProvider>
                  <AuthStateProvider>
                    <UserContextProvider>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MzSnackbarProvider />
                        <Routes>
                          <Route path={routes.claimBusiness} element={IdleTimerWrapper(<ClaimBusiness />)} />
                          <Route path={routes.auth.signOut} element={<SignOut />} />
                          <Route path="/auth" element={<LoginPage />} />
                          <Route path={routes.auth.signOut} element={<SignOut />} />
                          <Route path={routes.auth.forgotPassword} element={<ForgotPassword />} />
                          <Route path={routes.home} exact element={IdleTimerWrapper(<LandingPage />)} />
                          <Route path={routes.myAccount} exact element={IdleTimerWrapper(<MyAccount />)} />
                          <Route path={routes.myBusiness} exact element={IdleTimerWrapper(<MyBusiness />)} />
                          <Route path={routes.find} exact element={IdleTimerWrapper(<BusinessLookupPage />)} />
                          <Route path={routes.goldenRecord} exact element={IdleTimerWrapper(<GoldenRecordsPage />)} />
                          <Route path={routes.matchConfidenceSettings} exact element={IdleTimerWrapper(<MatchConfidencePage />)} />
                          <Route path={routes.contractTerms} exact element={IdleTimerWrapper(<ContractTermsPage />)} />
                          <Route path={routes.fileValidation} exact element={IdleTimerWrapper(<FileValidationPage />)} />
                          <Route path={routes.decisionSettings} exact element={IdleTimerWrapper(<DecisioningPage />)} />
                          <Route path={routes.p2bSettings} exact element={IdleTimerWrapper(<NameMatchingPage />)} />
                          <Route path={routes.userManagement} exact element={IdleTimerWrapper(<UserManagement />)} />
                          <Route path={routes.monitoringReportsPage} exact element={IdleTimerWrapper(<MonitoringReportsPage />)} />
                          <Route path={routes.batchSummary} exact element={IdleTimerWrapper(<BatchConfigurationPage />)} />
                          <Route
                            path={routes.vaultVerification}
                            exact
                            element={IdleTimerWrapper(<VaultVerification />, true)}
                          />
                          <Route path={routes.welcome} exact element={IdleTimerWrapper(<WelcomePage />)} />
                          <Route path={routes.vault} exact element={IdleTimerWrapper(<VaultPage />, true)} />
                          <Route path={routes.social} exact element={IdleTimerWrapper(<SocialPage />)} />
                          <Route path="/auth">
                            <Route path="/auth/EP/:EPSGId" element={<EmailVerify />} />
                          </Route>
                          <Route path="/auth" element={<AuthLayout />}>
                            <Route path="/auth/verify-password" element={<PasswordVerify />} />
                            <Route path="/auth/resend-mail" element={<ResendEmail />} />
                            <Route path="/auth/create-password" element={<CreatePassword />} />
                            <Route path="/auth/verify-otp" element={<OTPVerify />} />
                            <Route exact path="/auth/mobile/sign-up" element={<NameVerifyMob />} />
                            <Route exact path="/auth/mobile/verified" element={<MobileCheck />} />
                            <Route path="/auth/mobile/password" element={<PasswordVerifyMob />} />
                            <Route path="/auth/mobile/verify-otp" element={<AccountVerificationMob />} />
                            <Route path="/auth/mobileCheck" element={<MobileCheck />} />
                          </Route>
                          <Route path="/use-browser" element={<UseBrowserLayout />}>
                            <Route index element={<MobileCheck />} />
                          </Route>
                          <Route
                            path={routes.ownerBeneficialForm}
                            exact
                            element={<BeneficialForm individialForm={false} />}
                          />
                          <Route path="/articlesharing/:shareapproveid/approved" element={<ApproveShare />} />
                          <Route path="/articlesharing/:sharedeclineid/declined" element={<DeclineShare />} />
                          <Route path={routes.details} element={IdleTimerWrapper(<BusinessDetails />)} />
                          <Route path={routes.claim} element={IdleTimerWrapper(<ClaimBusinessDialog />)} />
                          <Route
                            path={routes.resources}
                            exact
                            element={IdleTimerWrapper(<BusinessResourceCenterCard />)}
                          />
                        </Routes>
                      </LocalizationProvider>
                    </UserContextProvider>
                  </AuthStateProvider>
                </AppStateProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </GoogleAnalyticsWrapper>
        </Router>
      </I18nextProvider>
    )
  }
}

export default App
